import type {
  ListingFacetKeys,
  ListingSortDirection,
  ListingSortKeys,
} from "src/types/adapters/listing.type";
import type {
  BrowseBrandsFacet,
  BrowseCategoriesFacet,
  BrowseEntity,
  BrowseProductsFacet,
  CollectionData,
  SearchCategoryData,
} from "src/types/adapters/browse.type";
import { CONSTRUCTOR_SECTIONS } from "src/constants/constructor";
import type {
  ConstructorProductData,
  ConstructorSectionKeys,
  ConstructorSuggestionData,
} from "./types";
import type { Suggestion } from "src/types/adapters/query.type";

export const PRODUCT_SUGGESTION_DATA_FIELDS = [
  "brand_id",
  "brand_name",
  "container_sizes",
  "currency",
  "gate",
  "genders",
  "id",
  "image",
  "labels",
  "price",
  "regular_price",
  "slug",
] as const satisfies readonly (keyof ConstructorProductData)[];

export const SUGGESTION_DATA_FIELDS = [
  "parent_path_string",
  "gate",
  "id",
  "slug",
  "type",
  "url",
  "query",
] as const satisfies readonly (keyof ConstructorSuggestionData)[];

export const CONSTRUCTOR_COLLECTIONS_DATA_FIELDS = [
  "id",
  "genders",
  "slug",
  "type",
  "url",
] as const satisfies readonly (keyof CollectionData)[];

export const CONSTRUCTOR_BRAND_DATA_FIELDS = [
  "id",
  "slug",
  "url",
  "gate",
] as const satisfies readonly (keyof ConstructorSuggestionData)[];

export const CONSTRUCTOR_CATEGORY_DATA_FIELDS = [
  "id",
  "slug",
  "url",
] as const satisfies readonly (keyof ConstructorSuggestionData)[];

export const PRODUCT_LIST_DATA_FIELDS = [
  "available_sizes_label",
  "brand_id",
  "brand_info",
  "brand_name",
  "categories",
  "color",
  "container_sizes",
  "currency",
  "gate",
  "genders",
  "id",
  "image",
  "labels",
  "price",
  "regular_price",
  "ot_product_id",
  "slug",
  "stock_amount",
  "woo_id",
] as const satisfies readonly (keyof ConstructorProductData)[];

export const LISTING_FACET_TO_CONSTRUCTOR = {
  brands: "brand_info.slug",
  category: "category_slugs",
  colors: "color.id",
  discount: "discount_bucket_ids",
  genders: "genders",
  pattern: "pattern.id",
  price: "price",
  size: "container_size_ids",
  style: "style.id",
  collections: "collections",
} as const satisfies Record<ListingFacetKeys, string>;

export const CONSTRUCTOR_TO_LISTING_FACET = Object.fromEntries(
  Object.entries(LISTING_FACET_TO_CONSTRUCTOR).map(([k, v]) => [v, k])
) as {
  [K in keyof typeof LISTING_FACET_TO_CONSTRUCTOR as (typeof LISTING_FACET_TO_CONSTRUCTOR)[K]]: K;
};

export const CONSTRUCTOR_TO_LISTING_SORT_KEY: Record<
  string,
  { key: ListingSortKeys; direction: ListingSortDirection }[]
> = {
  relevance: [{ key: "popularity", direction: "descending" }],
  new_in_score: [{ key: "new", direction: "descending" }],
  price: [
    { key: "price-low-to-high", direction: "ascending" },
    { key: "price-high-to-low", direction: "descending" },
  ],
  discount_date: [{ key: "new-discount", direction: "descending" }],
};

export const LISTING_TO_CONSTRUCTOR_SORT_KEY = {
  "new-discount": "discount_date",
  new: "new_in_score",
  "price-high-to-low": "price",
  "price-low-to-high": "price",
  popularity: "relevance",
} as const satisfies Partial<
  Record<ListingSortKeys, keyof typeof CONSTRUCTOR_TO_LISTING_SORT_KEY>
>;

export const DEFAULT_SORT_KEY = LISTING_TO_CONSTRUCTOR_SORT_KEY.popularity;
export const DEFAULT_SORT_DIRECTION: ListingSortDirection = "descending";

export const CONSTRUCTOR_FACETS = {
  PROMOTION_FILTER: "promotion_filter_ids",
  IS_DELETED: "is_deleted",
  DEPARTMENTS: "brand_info.departments",
  SALE: "is_clearance_product",
  DESIGNER_ONLY: "brand_info.designer_only",
  COLLECTIONS: "collections",
  LABEL_TYPE: "label_types",
  GROUP_ID: "group_id",
  // Native Constructor Collections feature filter (collection_id = the
  // Contentful slug). Distinct from COLLECTIONS above, which backs the
  // dual-run fallback and stays live until every collection is migrated.
  COLLECTION_ID: "collection_id",
} as const;

export const CONSTRUCTOR_PROMOTION_TYPE = {
  HOT_DEALS: "HOT_DEALS",
  NEW_IN: "NEW_IN",
} as const;

export const CONSTRUCTOR_DEPARTMENTS = {
  DESIGNER: "designer",
} as const;

export const DEFAULT_FILTERS = {
  [CONSTRUCTOR_FACETS.IS_DELETED]: "False",
  [CONSTRUCTOR_FACETS.DESIGNER_ONLY]: "False",
} as const;

export const CONSTRUCTOR_LABEL_TYPE = {
  NEW: "new",
};

export const BROWSE_ENTITY_TO_CONSTRUCTOR_SECTION = {
  products: CONSTRUCTOR_SECTIONS.PRODUCTS,
  brands: CONSTRUCTOR_SECTIONS.BRANDS,
  "all-brands": CONSTRUCTOR_SECTIONS.PRODUCTS,
  facets: CONSTRUCTOR_SECTIONS.PRODUCTS,
  categories: CONSTRUCTOR_SECTIONS.CATEGORIES,
  search_suggestions: CONSTRUCTOR_SECTIONS.SEARCH_SUGGESTIONS,
} as const satisfies Record<
  BrowseEntity,
  (typeof CONSTRUCTOR_SECTIONS)[keyof typeof CONSTRUCTOR_SECTIONS]
>;

export const BROWSE_PRODUCTS_FACET_TO_CONSTRUCTOR = {
  ...LISTING_FACET_TO_CONSTRUCTOR,
  is_deleted: "is_deleted",
} as const satisfies Partial<Record<BrowseProductsFacet, string>>;

export const BROWSE_BRANDS_FACET_TO_CONSTRUCTOR = {
  slug: "slug",
  is_deleted: CONSTRUCTOR_FACETS.IS_DELETED,
} as const satisfies Record<BrowseBrandsFacet, string>;

export const BROWSE_CATEGORIES_FACET_TO_CONSTRUCTOR = {
  slug: "slug",
  id: "id",
  // Already a configured filter-only facet on the Categories section
  // (event-engine CATEGORIES_FACETS), used to resolve a shop type's root
  // browse group without going through a localized slug.
  genders: "genders",
} as const satisfies Record<BrowseCategoriesFacet, string>;

export const CATEGORY_FIELDS = [
  "slug",
  "image",
  "woo_id",
  "parent",
  "is_deleted",
  "category_page_id",
  "parent_path_string",
  "excluded_from_filters",
] as const satisfies readonly (keyof SearchCategoryData)[];

export const MAP_SECTION_TO_SUGGESTION_TYPE: Partial<
  Record<ConstructorSectionKeys, Suggestion["type"]>
> = {
  Brands: "brand",
  Categories: "category",
  Collections: "collection",
} as const;

export const SUGGESTION_TYPE_ORDER = [
  "brand",
  "category",
  "collection",
] as const satisfies readonly Suggestion["type"][];

export const DEFAULT_MAX_SUGGESTIONS = 5;

export const DEFAULT_FACET = {
  facetName: CONSTRUCTOR_FACETS.IS_DELETED,
  facetValue: "False",
};

export const CONSTRUCTOR_CAP_RESULTS = 10000;
